import {
  audienceFromIdentity
} from "./chunk-GQVSQN44.js";
import {
  GENERATED_DEFAULTS,
  config,
  defaultForEnvironment,
  evaluationUserFromAudience,
  flagOverridesFromEnvironment
} from "./chunk-ZGRZS3TV.js";
import {
  FALLBACK_FLAGS_PROVIDER_ID,
  FlagsContext,
  REMOTE_FLAGS_PROVIDER_ID
} from "./chunk-AP3IQJFK.js";

// src/react/flags.provider.tsx
import {
  ConfigCatProvider,
  createFlagOverridesFromMap,
  OverrideBehaviour,
  PollingMode
} from "configcat-react";

// src/react/generated/browser-config.generated.ts
var GENERATED_BROWSER_CONFIG = {
  development: {
    provider: "configcat",
    sdkKey: "configcat-sdk-1/heLeCAjc1UiHkvnULrKnbA/6jBf6_mH70isyWH_EYOe0A"
  },
  uat: {
    provider: "configcat",
    sdkKey: "configcat-sdk-1/heLeCAjc1UiHkvnULrKnbA/rB4Zcer09Em_Zl9z1lqL5w"
  },
  production: {
    provider: "configcat",
    sdkKey: "configcat-sdk-1/heLeCAjc1UiHkvnULrKnbA/CIZ87Nv3cUOFU7uetWenUw"
  }
};

// src/react/browser-flags.config.ts
function browserFlagsConfigForEnvironment(environment, config2 = GENERATED_BROWSER_CONFIG) {
  return config2[environment];
}

// src/react/flags.provider.tsx
import { jsx } from "react/jsx-runtime";
function FlagsProvider({
  application,
  environment,
  identityState,
  children
}) {
  const browserConfig = browserFlagsConfigForEnvironment(environment);
  const remoteAvailable = isRemoteAvailable(browserConfig);
  const remoteEnabled = identityState.status === "ready" && remoteAvailable;
  const user = identityState.status === "ready" ? evaluationUserFromAudience(application, audienceFromIdentity(identityState.identity)) : void 0;
  const context = {
    environment,
    identityState,
    providerId: remoteEnabled ? REMOTE_FLAGS_PROVIDER_ID : FALLBACK_FLAGS_PROVIDER_ID,
    remoteEnabled,
    user
  };
  const fallbackOverrides = { ...GENERATED_DEFAULTS[environment] };
  const fallbackOptions = {
    offline: true,
    flagOverrides: createFlagOverridesFromMap(fallbackOverrides, OverrideBehaviour.LocalOnly)
  };
  const remoteOptions = remoteAvailable ? {
    cacheTimeToLiveSeconds: config.configCat.defaultCacheTimeToLiveSeconds,
    flagOverrides: createFlagOverridesFromMap(
      flagOverridesFromEnvironment(),
      OverrideBehaviour.LocalOverRemote
    )
  } : fallbackOptions;
  return /* @__PURE__ */ jsx(FlagsContext.Provider, { value: context, children: /* @__PURE__ */ jsx(
    ConfigCatProvider,
    {
      id: FALLBACK_FLAGS_PROVIDER_ID,
      sdkKey: FALLBACK_SDK_KEYS[environment],
      pollingMode: PollingMode.ManualPoll,
      options: fallbackOptions,
      children: /* @__PURE__ */ jsx(
        ConfigCatProvider,
        {
          id: REMOTE_FLAGS_PROVIDER_ID,
          sdkKey: remoteAvailable ? browserConfig.sdkKey : INACTIVE_REMOTE_SDK_KEYS[environment],
          pollingMode: remoteAvailable ? PollingMode.LazyLoad : PollingMode.ManualPoll,
          options: remoteOptions,
          children
        }
      )
    }
  ) });
}
function isRemoteAvailable(browserConfig) {
  return browserConfig.provider === "configcat" && browserConfig.sdkKey.trim() !== "";
}
var FALLBACK_SDK_KEYS = {
  development: `${"0".repeat(21)}1/${"0".repeat(21)}1`,
  uat: `${"0".repeat(21)}2/${"0".repeat(21)}2`,
  production: `${"0".repeat(21)}3/${"0".repeat(21)}3`
};
var INACTIVE_REMOTE_SDK_KEYS = {
  development: `${"0".repeat(21)}4/${"0".repeat(21)}4`,
  uat: `${"0".repeat(21)}5/${"0".repeat(21)}5`,
  production: `${"0".repeat(21)}6/${"0".repeat(21)}6`
};

// src/react/use-flag.hook.ts
import { useContext, useEffect, useState } from "react";
import { useConfigCatClient } from "configcat-react";
function useFlag(key) {
  const context = useContext(FlagsContext);
  const client = useConfigCatClient(context.providerId);
  const fallback = defaultForEnvironment(context.environment, key);
  const serialisedUser = context.user === void 0 ? void 0 : JSON.stringify(context.user);
  const fingerprint = {
    client,
    providerId: context.providerId,
    key,
    fallback,
    serialisedUser
  };
  const [evaluation, setEvaluation] = useState({
    fingerprint,
    value: fallback,
    loading: context.remoteEnabled
  });
  const evaluateRemotely = context.remoteEnabled && context.identityState.status === "ready";
  useEffect(() => {
    const evaluationUser = context.user;
    if (!evaluateRemotely) {
      setEvaluation(
        (current) => fingerprintsMatch(current.fingerprint, fingerprint) && Object.is(current.value, fallback) && !current.loading ? current : { fingerprint, value: fallback, loading: false }
      );
      return;
    }
    let cancelled = false;
    let evaluationSequence = 0;
    const evaluate = () => {
      const sequence = ++evaluationSequence;
      client.getValueAsync(key, fallback, evaluationUser).then(
        (value) => {
          if (cancelled || sequence !== evaluationSequence) return;
          setEvaluation(
            (current) => fingerprintsMatch(current.fingerprint, fingerprint) ? { fingerprint, value, loading: false } : current
          );
        },
        () => {
          if (cancelled || sequence !== evaluationSequence) return;
          setEvaluation(
            (current) => fingerprintsMatch(current.fingerprint, fingerprint) ? { fingerprint, value: fallback, loading: false } : current
          );
        }
      );
    };
    const handleConfigChanged = () => {
      if (cancelled) return;
      setEvaluation(
        (current) => fingerprintsMatch(current.fingerprint, fingerprint) ? { fingerprint, value: fallback, loading: true } : current
      );
      evaluate();
    };
    setEvaluation(
      (current) => fingerprintsMatch(current.fingerprint, fingerprint) && current.loading ? current : { fingerprint, value: fallback, loading: true }
    );
    client.on("configChanged", handleConfigChanged);
    evaluate();
    return () => {
      cancelled = true;
      client.off("configChanged", handleConfigChanged);
    };
  }, [client, context.providerId, evaluateRemotely, fallback, key, serialisedUser]);
  if (context.identityState.status === "loading") {
    return [fallback, true];
  }
  if (!context.remoteEnabled || context.identityState.status === "failed") {
    return [fallback, false];
  }
  if (!fingerprintsMatch(evaluation.fingerprint, fingerprint)) {
    return [fallback, true];
  }
  return [evaluation.value, evaluation.loading];
}
function fingerprintsMatch(left, right) {
  return left.client === right.client && left.providerId === right.providerId && left.key === right.key && Object.is(left.fallback, right.fallback) && left.serialisedUser === right.serialisedUser;
}
export {
  FlagsProvider,
  useFlag
};
//# sourceMappingURL=react.js.map